import { registerPropertyManagerSection } from '@/core';
import MoveComponentPropertySection from '@/components/editor/property-manager/move-component/MoveComponentPropertySection.vue';
import { useEditorAssemblyWorkflow } from '@/views/editor/useEditorAssemblyWorkflow';

interface MoveComponentControllerContract {
  isActive?: () => boolean;
  getToolMode?: () => string;
  commit?: () => void | Promise<void>;
  cancel?: () => void;
}

interface MoveComponentViewerRuntime {
  moveComponentController?: MoveComponentControllerContract;
}

function resolveMoveComponentController(context: { renderer?: unknown }): MoveComponentControllerContract | null {
  const runtime = context.renderer as MoveComponentViewerRuntime | null | undefined;
  return runtime?.moveComponentController ?? null;
}

/**
 * 注册"移动/旋转零部件"属性面板。
 *
 * isApplicable 规则：
 * 1. activeCommand === 'assembly-move-component' 表示工具从命令入口激活；
 * 2. activeCommand === 'assembly-rotate-component' 表示旋转工具从命令入口激活；
 * 3. 装配体模式下控制器可能会被静默激活以支持默认拖拽，但这不应自动显示 PropertyManager。
 */
registerPropertyManagerSection({
  id: 'assembly-move-component',
  title: (context) => {
    // 优先读取控制器当前的 toolMode，以便面板内部切换也能同步标题。
    const ctrl = resolveMoveComponentController(context);
    const toolMode = ctrl?.getToolMode?.();
    if (toolMode === 'rotate') {
      return '旋转零部件';
    }
    if (toolMode === 'move') {
      return '移动零部件';
    }
    // 回退：依赖命令入口。
    return context.activeCommand === 'assembly-rotate-component' ? '旋转零部件' : '移动零部件';
  },
  order: 50,
  component: MoveComponentPropertySection,
  actions: {
    confirm: true,
    cancel: true,
  },
  isApplicable: (context) =>
    context.activeCommand === 'assembly-move-component'
    || context.activeCommand === 'assembly-rotate-component',
  onConfirm: () => {
    // 通过工作流统一提交，确保模式切回 Assembly 并同步工具栏状态。
    void useEditorAssemblyWorkflow().confirmAssemblyTransformCommand().catch((error) => {
      if (typeof window !== 'undefined' && typeof window.reportError === 'function') {
        window.reportError(error);
      }
    });
  },
  onCancel: () => {
    // 通过工作流统一取消，确保模式切回 Assembly 并同步工具栏状态。
    useEditorAssemblyWorkflow().cancelAssemblyTransformCommand();
  },
});
